<template>
    <form ref="form" class="form-material" enctype="multipart/form-data" @submit.prevent="submit"
          @click="form.errors.clear()">
        <div class="row">
            <div class="col-12">
                <status-progress-bar
                        class="mt-3"
                        v-if="loaded"
                        :current="revenueUpdate.state"
                        :states="stateSequence"
                        :processing="isProcessing"
                        :onlyTooltip="false">
                </status-progress-bar>
            </div>
        </div>
        <div class="card" v-if="!loaded">
            <div class="card-header">
                Loading...
            </div>
        </div>
        <div class="card" v-if="loaded">
            <div class="card-header">
                <div class="mb-2">
                    <h4 class="card-title mb-0 nowrap mr-1 d-inline">
                        {{ date }}
                    </h4>
                    <h4 class="card-title d-inline text-primary">
                        / {{ title }}
                    </h4>
                </div>
                <div class="card-subtitle text-muted" v-html="subtitle"></div>
            </div>
            <div class="card-body ninja-playground">
                <div v-html="step.getHtml()"></div>
                <div v-if="links.length > 0" class="mb-3">
                    <div v-for="link of links" class="step-link">
                        <a :href="link.url" target="_blank">
                            <span class="icon ion-ios-link mr-2"></span><span v-html="link.label"></span>
                        </a>
                    </div>
                </div>
                <div class="mb-4" v-if="helpText.length > 0">
                    <div class="text-muted mb-2"><em>Manual Instructions:</em></div>
                    <ul class="help-text">
                        <li v-for="item in helpText" v-html="item"></li>
                    </ul>
                </div>
                <div class="form-group mb-1" v-if="hasForm">
                    <input type="file" name="file" class="form-control-file" @change="fileChange" required>
                    <span class="help is-danger" v-if="form.errors.has('file')"
                          v-text="form.errors.get('file')"></span>
                </div>
                <revenue-update-validation v-if="revenueUpdate.state === 'validate-revenue'"
                                           :id="this.id"></revenue-update-validation>
                <t-ninja ref="ninja" :status="ninjaStatus"></t-ninja>
            </div>
            <div class="card-footer d-flex justify-content-between">
                <fieldset :disabled="waiting">
                    <button type="submit" class="btn btn-primary" v-if="canProceed" :disabled="locked">
                        <template v-if="hasForm">
                            {{ 'admin.import' | lang }}
                        </template>
                        <template v-else>
                            {{ 'general.proceed' | lang }}
                        </template>
                    </button>
                    <div class="dropup d-inline-block" v-if="rollbackSequence.length > 0">
                        <button class="btn btn-secondary" type="button" id="dropdownMenuButton" :disabled="!canRollback"
                                data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                            <span class="fas fa-history"></span>
                        </button>
                        <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
                            <a v-for="(value, index) of rollbackSequence" :key="index"
                               class="dropdown-item" href="#" @click="rollbackTo(value.state)">
                                <span v-html="value.label"></span>
                                <em v-if="index === 0">(start)</em>
                                <em v-else-if="index === rollbackSequence.length-1">(previous)</em>
                            </a>
                        </div>
                        <small class="descriptive-block" v-if="!revenueUpdate.canRollback">
                            Unable to rollback when revenue has been sent to prosperity.
                        </small>
                    </div>
                </fieldset>
                <div>
                    <button class="btn btn-secondary" @click="close">
                        Back
                    </button>
                </div>
            </div>
            <t-loader :locked="loading" :type="rollback ? 'reverse' : 'normal'"></t-loader>
        </div>
        <div v-if="this.errors.length > 0">
            <div class="text-dark small">Error{{ this.errors.length > 1 ? 's' : ''}}:</div>
            <t-error v-for="(terror, index) of errors" :key="index" :error="terror"></t-error>
        </div>
    </form>
</template>

<script>
  import axios from 'axios';
  import TForm from '#/js/components/TForm';
  import TToast from '@/js/components/TToast';
  import TError from '@/js/components/TError';
  import StatusProgressBar from "#/js/components/StatusProgressBar";
  import WizardCloudHandler from "#/js/components/revenue/WizardCloudHandler";
  import WizardStep from "#/js/components/revenue/steps/WizardStep";
  import ImportPaidFeaturesStep from "#/js/components/revenue/steps/ImportPaidFeaturesStep";
  import ImportRedMusicStep from "#/js/components/revenue/steps/ImportRedMusicStep";
  import ImportRedNonMusicStep from "#/js/components/revenue/steps/ImportRedNonMusicStep";
  import ImportAffiliateTaxStep from '#/js/components/revenue/steps/ImportAffiliateTaxStep';
  import ImportShortsAdsStep from '#/js/components/revenue/steps/ImportShortsAdsStep';
  import ImportShortsSubscriptionStep from '#/js/components/revenue/steps/ImportShortsSubscriptionStep';
  import ImportChannelAdjustmentsStep from '#/js/components/revenue/steps/ImportChannelAdjustmentsStep';
  import ProcessBigQueryState from "#/js/components/revenue/steps/ProcessBigQueryState";
  import ProcessRevenueStep from "#/js/components/revenue/steps/ProcessRevenueStep";
  import CompletedStep from "#/js/components/revenue/steps/CompletedStep";
  import FailedStep from "#/js/components/revenue/steps/FailedStep";
  import ValidateRevenueStep from "#/js/components/revenue/steps/ValidateRevenue";
  import StatusProgressState from "#/js/components/StatusProgressState";
  import RevenueUpdateValidation from "#/js/components/revenue/RevenueUpdateValidation";
  import FormHelper from "#/js/helpers/FormHelper";
  import TNinja from "#/js/components/TNinja";

  export default {
    name: "RevenueUpdateWizard",
    components: {
      TNinja,
      RevenueUpdateValidation,
      StatusProgressBar,
      TError,
    },
    props: {
      id: Number,
    },
    data() {
      return {
        form: new TForm({
          file: '',
        }, false),
        revenueUpdate: null,
        steps: {
          'import-paid-features': ImportPaidFeaturesStep,
          'import-music': ImportRedMusicStep,
          'import-non-music': ImportRedNonMusicStep,
          'import-affiliate-tax': ImportAffiliateTaxStep,
          'import-shorts-ads': ImportShortsAdsStep,
          'import-shorts-subscription': ImportShortsSubscriptionStep,
          'import-channel-adjustments': ImportChannelAdjustmentsStep,
          'process-big-query': ProcessBigQueryState,
          'process-revenue': ProcessRevenueStep,
          'validate-revenue': ValidateRevenueStep,
          'completed': CompletedStep,
        },
        step: null,
        toast: new TToast(),
        process: null,
        processing: false,
        rollback: false,
        waiting: true,
        errors: [],
      }
    },
    mounted() {
      Echo.private(this.channel)
        .listen('RevenueUpdateProcessChange', (data) => {
          this.onProcessChange(data.process, data.state);
        })
        .listen('RevenueUpdateChange', (data) => {
          const revenueUpdate = data.revenueUpdate;
          this.onRevenueUpdateChange(revenueUpdate);
        });

      this.getRevenueUpdate();
    },
    beforeDestroy() {
      this.form.reset();
      Echo.private(this.channel).stopListening('RevenueUpdateProcessChange').stopListening('RevenueUpdateChange');
    },
    computed: {
      channel() {
        return `revenueUpdate.${this.id}`;
      },
      loading() {
        return !this.loaded || this.isProcessing || this.waiting;
      },
      isProcessing() {
        if (this.rollback) {
          return false;
        }
        if (this.process) {
          switch (this.process.state) {
            case 'active':
            case 'reserved':
              return true;
          }
        }

        return this.revenueUpdate.isProcessing || this.processing;
      },
      loaded() {
        return this.step && this.revenueUpdate;
      },
      ninjaStatus() {
        const state = this.revenueUpdate.state;
        if ((this.step.estimate ?? 0) >= 4) {
          return 'coffee';
        }
        if (this.loading) {
          return this.isProcessing ? 'focus' : 'sleeping';
        }

        return state === 'completed' ? 'success' : 'default';
      },
      stateSequence() {
        return Object.keys(this.steps).map((state) => {
          return new StatusProgressState(state, this.getStateName(state))
        });
      },
      rollbackSequence() {
        return this.revenueUpdate.rollbackSequence.map((state) => {
          const name = this.getStateName(state);

          return {
            state: state,
            label: Lang.get(`admin.revenue.updates.rollback`, {state: `<strong>${name}</strong>`})
          };
        }).reverse();
      },
      canRollback() {
        return this.rollbackSequence.length > 0 && this.revenueUpdate.canRollback;
      },
      hasForm() {
        if (this.step) {
          return this.step.hasForm();
        }
        return false;
      },
      canProceed() {
        return this.stepType !== WizardStep.TYPE_NONE;
      },
      stepType() {
        return this.step.getType();
      },
      links() {
        return this.step.getLinks();
      },
      name() {
        return this.step.getName();
      },
      title() {
        return this.step.getTitle();
      },
      subtitle() {
        return this.step.getSubtitle();
      },
      helpText() {
        return this.step.getHelpText();
      },
      routeGet() {
        return `/admin/revenue/updates/${this.id}`;
      },
      locked() {
        if (this.hasForm) {
          const hasFile = this.form.file || false;
          return !hasFile || this.loading;
        }

        return this.loading;
      },
      date() {
        return moment.tz(this.revenueUpdate.target, 'YYYY-MM-DD', 'UTC').format('MMMM Y');
      },
    },
    methods: {
      getStateName(state) {
        return Lang.get(`admin.revenue.updates.states.${state}`);
      },
      rollbackTo(state) {
        const target = this.getStateName(state);
        const current = this.getStateName(this.revenueUpdate.state);
        const content = document.createElement('div');

        content.innerHTML = `
<div class="mb-3">Are you sure you want to rollback to<br><strong>${target}</strong>?</div>
<small>This will rollback all changes between the current step <em>${current}</em> to the rollback destination step <em>${target}</em></small>
`;

        swal(FormHelper.confirmSettings({
          title: 'Confirm Rollback',
          text: null,
          content: content,
        })).then((confirmed) => {
          if (!confirmed) {
            return;
          }

          this.waiting = true;
          this.rollback = true;
          axios.post(this.revenueUpdate.routes.rollback, {
            'state': state,
          }).catch(error => {
            this.log('Error during rollback', 'error');
            this.error(error);
            this.waiting = false;
            this.rollback = false;
          });
        });

      },
      resetForm() {
        this.errors = [];
        this.form.reset();
        if (this.$refs.form) {
          this.$refs.form.reset();
        }
      },
      updateStep(revenueUpdate) {
        this.resetForm();
        const state = revenueUpdate.state;
        if (!this.steps[state] instanceof constructor) {
          this.step = new FailedStep(revenueUpdate, {
            title: `Failed to find wizard step via given state: ${state}`,
          });
        }

        this.step = new this.steps[state](revenueUpdate);
      },
      onRevenueUpdateChange(revenueUpdate) {
        if (!this.step || this.revenueUpdate.state !== revenueUpdate.state) {
          this.updateStep(revenueUpdate);
        }
        this.revenueUpdate = revenueUpdate;
        this.process = null;
        this.waiting = false;
        this.processing = false;
        this.rollback = false;
      },
      onProcessChange(process, state) {
        this.process = process;

        switch (state) {
          case 'active':
            return;
          case 'failed':
            this.error(`Process ${process.name} failed.`);
            break;
          case 'completed':
            this.success(`Process ${process.name} completed.`);
            break;
        }

        this.processing = false;
      },
      close() {
        this.$emit('close');
      },
      fileChange(event) {
        const original = event.target.files[0];
        // Remove any unexpected excess from filename
        const filename = this.trimFileName(original.name);
        this.form.file = new File([original], filename, {type: original.type});
      },
      trimFileName(name) {
        // Fix paid features zip archives and cloud storage gz archives
        // Example: remove *space*(1) from "831_v1-1.csv (1).zip"
        name = name.replace(/\.csv(.*?)\.(zip|gz)$/u, '.csv.$2');

        return name.trim();
      },
      getRevenueUpdate() {
        this.waiting = true;
        axios.get(this.routeGet).then(response => {
          const revenueUpdate = response.data;
          this.onRevenueUpdateChange(revenueUpdate);
        }).catch(error => {
          this.log('Error retrieving', 'error');
          this.error(error);
        }).finally(() => {
          this.waiting = false;
        });
      },
      submit() {
        this.waiting = true;
        try {
          switch (this.stepType) {
            case WizardStep.TYPE_UPLOAD:
              this.uploadLocal();
              break;
            case WizardStep.TYPE_PROCESS:
              this.processLocal();
              break;
            case WizardStep.TYPE_CLOUD:
              this.uploadCloud();
              break;
            default:
              this.error('No such step type');
          }
        } catch (error) {
          this.error(error);
        }
      },
      processLocal() {
        this.processing = true;
        this.form.post(this.revenueUpdate.routes.process).then(response => {
          this.log(response);
        }).catch(error => {
          this.error(error);
        });
      },
      uploadLocal() {
        this.processing = true;
        this.form.post(this.revenueUpdate.routes.import, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }).then(response => {
          this.log(response);
        }).catch(error => {
          this.error(error);
        });
      },
      uploadCloud() {
        this.processing = true;
        const routes = this.revenueUpdate.routes;
        const handler = new WizardCloudHandler(this.form, {
          routes: routes,
        });

        handler.handle().catch(error => {
          this.error(error);
        });
      },
      log(message, type = null) {
        // console.log(message, type);
      },
      error(error) {
        this.errors = [];

        console.error(error);
        if (typeof error === 'object') {
          this.errors.push(error);
          error = error.message;
        }
        this.toast.error(error, {hideAfter: 8000});
        this.waiting = false;
        this.processing = false;
        this.$refs.ninja.failure();
      },
      success(message) {
        this.toast.success(message, {hideAfter: 6000});
        this.$refs.ninja.success();
      }
    }
  }
</script>

<style lang="scss">
    .revenue-wizard {
        max-width: 1000px;
    }

    .descriptive-block {
        max-width: 15rem;
        display: inline-block;
        vertical-align: bottom;
        margin-left: .5rem;
        color: #606068;
    }

    .step-link {
        a {
            font-weight: 500;
        }
    }

    .help-text {
        em {
            font-weight: 600;
        }
    }

    .swal-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .swal-title {
        padding-bottom: 0;
    }
</style>
